import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAdmin } from '../context/useAdmin';
import { NAME_MAX, STANDARD_LAYOUT_ID } from '../lib/access';
import type { NamedLayout } from '../lib/access';
import { saveLayout } from '../lib/adminApi';
import { useQueryNav } from '../lib/nav';
import { DEFAULT_LAYOUT, LABEL_MAX, SCREENS, newId, validateLayout } from '../lib/rails';
import type { NavRail, NavTab, ScreenKey } from '../lib/rails';
import { AdminShell } from './admin/AdminShell';

const SCREEN_KEYS = Object.keys(SCREENS) as ScreenKey[];

/** Offered to a new section in turn, so two neighbours never start the same colour. */
const COLORS = ['#1f6fb2', '#7b4bb3', '#0f766e', '#d03b34', '#e58a1a', '#2e9e4f'];

const NEW_TAB = 'New tab';

function move<T>(list: T[], index: number, by: -1 | 1): T[] {
  const to = index + by;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

/**
 * /web/Admin/Layout?id=… — the editor for one layout: its sections on the left
 * rail and the tabs inside each. Rename them, put them in another order, hide
 * or delete them, or add new ones. A tab shows one of the portal's screens; who
 * may open it still follows the role's permissions, not the section it sits
 * in. Which layout a person sees is decided by their role (pages/admin/Roles);
 * this page only arranges the layout itself.
 */
export function LayoutAdmin() {
  const { store } = useAdmin();
  const { params } = useQueryNav();

  // The bare address is the standard layout, which is where the old link went.
  const id = Number(params.get('id')) || STANDARD_LAYOUT_ID;
  const record = store.layouts.find((l) => l.id === id);

  return (
    <AdminShell active="Layouts" title="Layout Management" count={record?.name}>
      {/* Keyed by layout, so opening another one starts from its own draft. */}
      {record ? <LayoutEditor key={record.id} record={record} /> : <Navigate to="/web/Admin/Layouts" replace />}
    </AdminShell>
  );
}

/** Edits are a draft until Save, so a half-finished rearrangement is never what people are looking at. */
function LayoutEditor({ record }: { record: NamedLayout }) {
  const { store, commit } = useAdmin();

  const [name, setName] = useState(record.name);
  const [draft, setDraft] = useState<{ rails: NavRail[] }>({ rails: record.rails });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Which "really delete?" is open — a section, a tab, or "reset". One at a time. */
  const [asking, setAsking] = useState<string | null>(null);

  const problems = useMemo(() => validateLayout(draft), [draft]);
  const dirty = useMemo(
    () => name !== record.name || JSON.stringify(draft.rails) !== JSON.stringify(record.rails),
    [name, draft, record],
  );

  const usedBy = store.roles.filter((r) => r.layoutId === record.id).map((r) => r.name);
  if (record.id === STANDARD_LAYOUT_ID) usedBy.unshift('Superadmin');

  function edit(change: (rails: NavRail[]) => NavRail[]) {
    setDraft((d) => ({ rails: change(d.rails) }));
    setNotice(null);
    setError(null);
    setAsking(null);
  }

  const patchRail = (id: string, patch: Partial<NavRail>) =>
    edit((rails) => rails.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const patchTabs = (railId: string, change: (tabs: NavTab[]) => NavTab[]) =>
    edit((rails) => rails.map((r) => (r.id === railId ? { ...r, tabs: change(r.tabs) } : r)));

  const patchTab = (railId: string, tabId: string, patch: Partial<NavTab>) =>
    patchTabs(railId, (tabs) => tabs.map((t) => (t.id === tabId ? { ...t, ...patch } : t)));

  function addRail() {
    edit((rails) => [
      ...rails,
      {
        id: newId('rail'),
        label: 'New section',
        color: COLORS[rails.length % COLORS.length],
        hidden: false,
        tabs: [{ id: newId('tab'), label: SCREENS.dashboard.label, screen: 'dashboard', hidden: false }],
      },
    ]);
  }

  function addTab(railId: string) {
    patchTabs(railId, (tabs) => [
      ...tabs,
      { id: newId('tab'), label: NEW_TAB, screen: 'dashboard', hidden: false },
    ]);
  }

  /** A tab still carrying a default name is renamed along with its screen. */
  function changeScreen(railId: string, t: NavTab, screen: ScreenKey) {
    const untouched = t.label === NEW_TAB || t.label === SCREENS[t.screen].label;
    patchTab(railId, t.id, { screen, label: untouched ? SCREENS[screen].label : t.label });
  }

  async function onSave() {
    if (busy || problems.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const next = await commit(saveLayout(record.id, name, draft));
      const saved = next.layouts.find((l) => l.id === record.id);
      if (saved) {
        setName(saved.name);
        setDraft({ rails: saved.rails });
      }
      setNotice(
        usedBy.length > 0
          ? `Saved. ${usedBy.join(', ')} now ${usedBy.length === 1 ? 'sees' : 'see'} this layout.`
          : 'Saved. No role uses this layout yet; pick it on a role to put it to work.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the layout.');
    } finally {
      setBusy(false);
    }
  }

  /** Back to the sections and tabs the portal ships with — still a draft until saved. */
  function onReset() {
    setDraft({ rails: structuredClone(DEFAULT_LAYOUT.rails) });
    setNotice(null);
    setError(null);
    setAsking(null);
  }

  function onDiscard() {
    setName(record.name);
    setDraft({ rails: record.rails });
    setNotice(null);
    setError(null);
    setAsking(null);
  }

  return (
    <div className="layouteditor">
        <header className="layouthead">
          <Link to="/web/Admin/Layouts" className="adminback">
            ‹ All layouts
          </Link>
          <label className="layouttitle">
            <span>Layout name</span>
            <input
              type="text"
              className="layoutname layoutname--rail"
              value={name}
              maxLength={NAME_MAX}
              onChange={(e) => {
                setName(e.target.value);
                setNotice(null);
                setError(null);
              }}
            />
          </label>
          <p className="cmmuted layoutusedby">
            {usedBy.length > 0 ? `Seen by: ${usedBy.join(', ')}` : 'No role uses this layout yet.'}
          </p>
        </header>

        <div className="layoutbar">
          <button type="button" className="cmbtn" onClick={addRail} disabled={busy}>
            + Add section
          </button>
          <span className="cmspacer" />
          <span className="layoutstate" role="status">
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </span>

          {asking === 'reset' ? (
            <span className="layoutask">
              Replace these sections with the portal&apos;s default ones?
              <button type="button" className="cmbtn cmbtn--danger" onClick={onReset} disabled={busy}>
                Replace
              </button>
              <button type="button" className="cmbtn" onClick={() => setAsking(null)}>
                Keep
              </button>
            </span>
          ) : (
            <button type="button" className="cmbtn" onClick={() => setAsking('reset')} disabled={busy}>
              Start again from default
            </button>
          )}

          <button type="button" className="cmbtn" onClick={onDiscard} disabled={busy || !dirty}>
            Discard
          </button>
          <button
            type="button"
            className="cmbtn cmbtn--primary"
            onClick={onSave}
            disabled={busy || !dirty || problems.length > 0}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {notice && !error && (
          <div className="notice" role="status">
            {notice} <Link to="/web">Open the portal</Link>
          </div>
        )}
        {problems.length > 0 && (
          <ul className="layoutproblems" role="alert">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}

        <ol className="layoutrails">
          {draft.rails.map((rail, railIndex) => (
            <li
              key={rail.id}
              className={rail.hidden ? 'layoutrail is-hidden' : 'layoutrail'}
              aria-label={`Section ${railIndex + 1}`}
            >
              <div className="layoutrow layoutrow--rail">
                <input
                  type="color"
                  className="layoutcolor"
                  value={rail.color}
                  aria-label={`Colour of section ${railIndex + 1}`}
                  onChange={(e) => patchRail(rail.id, { color: e.target.value })}
                />
                <input
                  type="text"
                  className="layoutname layoutname--rail"
                  value={rail.label}
                  maxLength={LABEL_MAX}
                  placeholder="Section name"
                  aria-label={`Name of section ${railIndex + 1}`}
                  onChange={(e) => patchRail(rail.id, { label: e.target.value })}
                />
                <RowTools
                  what={`section ${railIndex + 1}`}
                  hidden={rail.hidden}
                  first={railIndex === 0}
                  last={railIndex === draft.rails.length - 1}
                  onHide={(hidden) => patchRail(rail.id, { hidden })}
                  onMove={(by) => edit((rails) => move(rails, railIndex, by))}
                  asking={asking === `rail:${rail.id}`}
                  question={`Delete “${rail.label || 'this section'}” and its ${rail.tabs.length} ${rail.tabs.length === 1 ? 'tab' : 'tabs'}?`}
                  onAsk={(on) => setAsking(on ? `rail:${rail.id}` : null)}
                  onDelete={() => edit((rails) => rails.filter((r) => r.id !== rail.id))}
                />
              </div>

              <ol className="layouttabs">
                {rail.tabs.map((t, tabIndex) => (
                  <li
                    key={t.id}
                    className={t.hidden ? 'layoutrow is-hidden' : 'layoutrow'}
                    aria-label={`Tab ${tabIndex + 1} of section ${railIndex + 1}`}
                  >
                    <input
                      type="text"
                      className="layoutname"
                      value={t.label}
                      maxLength={LABEL_MAX}
                      placeholder="Tab name"
                      aria-label={`Name of tab ${tabIndex + 1} in section ${railIndex + 1}`}
                      onChange={(e) => patchTab(rail.id, t.id, { label: e.target.value })}
                    />
                    <label className="layoutscreen">
                      <span>shows</span>
                      <select
                        className="cmselect"
                        value={t.screen}
                        aria-label={`Screen of tab ${tabIndex + 1} in section ${railIndex + 1}`}
                        onChange={(e) => changeScreen(rail.id, t, e.target.value as ScreenKey)}
                      >
                        {SCREEN_KEYS.map((key) => (
                          <option key={key} value={key}>
                            {SCREENS[key].label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <RowTools
                      what={`tab ${tabIndex + 1} in section ${railIndex + 1}`}
                      hidden={t.hidden}
                      first={tabIndex === 0}
                      last={tabIndex === rail.tabs.length - 1}
                      onHide={(hidden) => patchTab(rail.id, t.id, { hidden })}
                      onMove={(by) => patchTabs(rail.id, (tabs) => move(tabs, tabIndex, by))}
                      asking={asking === `tab:${rail.id}:${t.id}`}
                      question={`Delete “${t.label || 'this tab'}”?`}
                      onAsk={(on) => setAsking(on ? `tab:${rail.id}:${t.id}` : null)}
                      onDelete={() => patchTabs(rail.id, (tabs) => tabs.filter((x) => x.id !== t.id))}
                    />
                  </li>
                ))}
              </ol>

              <button type="button" className="cmbtn layoutadd" onClick={() => addTab(rail.id)}>
                + Add tab
              </button>
            </li>
          ))}
        </ol>

        {draft.rails.length === 0 && (
          <p className="layoutempty">No sections. Add one, or start again from the default.</p>
        )}
    </div>
  );
}

interface RowToolsProps {
  /** Names the row for a screen reader: "tab 2 in section 1". */
  what: string;
  hidden: boolean;
  first: boolean;
  last: boolean;
  onHide: (hidden: boolean) => void;
  onMove: (by: -1 | 1) => void;
  asking: boolean;
  question: string;
  onAsk: (on: boolean) => void;
  onDelete: () => void;
}

/** Hide, move up, move down and delete — the same four for a section and a tab. */
function RowTools(props: RowToolsProps) {
  const { what, hidden, first, last, onHide, onMove, asking, question, onAsk, onDelete } = props;

  if (asking) {
    return (
      <span className="layoutask">
        {question}
        <button type="button" className="cmbtn cmbtn--danger" onClick={onDelete}>
          Delete
        </button>
        <button type="button" className="cmbtn" onClick={() => onAsk(false)}>
          Keep
        </button>
      </span>
    );
  }

  return (
    <span className="layouttools">
      <label className="layouthide">
        <input type="checkbox" checked={hidden} onChange={(e) => onHide(e.target.checked)} />
        <span>Hidden</span>
      </label>
      <button
        type="button"
        className="cmbtn layoutmove"
        aria-label={`Move ${what} up`}
        title="Move up"
        disabled={first}
        onClick={() => onMove(-1)}
      >
        ↑
      </button>
      <button
        type="button"
        className="cmbtn layoutmove"
        aria-label={`Move ${what} down`}
        title="Move down"
        disabled={last}
        onClick={() => onMove(1)}
      >
        ↓
      </button>
      <button
        type="button"
        className="cmbtn cmbtn--danger"
        aria-label={`Delete ${what}`}
        onClick={() => onAsk(true)}
      >
        Delete
      </button>
    </span>
  );
}
